const template = document.createElement('template');
template.innerHTML = `
<style>
    :host {
        display: inline-block;
        font-family: 'Courier New', Courier, monospace;
        user-select: none;
        --bg-color: #222;
        --thumb-color: #444;
        --active-color: #00ffcc;
        --text-color: #aaa;
        --active-text-color: #fff;
        --height: 30px;
        --width: 150px;
        --cols: auto;
    }
    .container {
        position: relative;
        width: var(--width);
        height: var(--height);
        background: var(--bg-color);
        border-radius: 4px;
        overflow: hidden;
        border: 1px solid #444;
        cursor: pointer;
        box-shadow: inset 0 1px 3px rgba(0,0,0,0.5);
    }
    .thumb {
        position: absolute;
        background: var(--thumb-color);
        border-radius: 3px;
        transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
        z-index: 1;
        border: 1px solid rgba(255,255,255,0.1);
        box-shadow: 0 1px 2px rgba(0,0,0,0.5);
    }
    :host([active]) .thumb {
        background: var(--active-color);
        opacity: 0.2;
    }
    
    .labels {
        position: relative;
        display: grid;
        grid-template-columns: repeat(var(--cols), 1fr);
        width: 100%;
        height: 100%;
        z-index: 2;
    }
    .label {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        color: var(--text-color);
        font-weight: bold;
        transition: color 0.2s;
        text-transform: uppercase;
        text-align: center;
        padding: 2px;
    }
    .label svg {
        width: 18px;
        height: 18px;
        display: block;
    }
    .label.active {
        color: var(--active-text-color);
        text-shadow: 0 0 5px var(--active-color);
    }

    .val-display {
        position: absolute;
        top: -22px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0,0,0,0.9);
        color: var(--active-color);
        padding: 2px 6px;
        font-size: 10px;
        border-radius: 3px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s, transform 0.2s;
        white-space: nowrap;
        z-index: 10;
        border: 1px solid rgba(255,255,255,0.1);
    }

    .container:hover .val-display,
    :host(:active) .val-display {
        opacity: 1;
        transform: translateX(-50%) translateY(-2px);
    }
</style>
<div class="container" id="container">
    <div class="val-display" id="val-display"></div>
    <div class="thumb" id="thumb"></div>
    <div class="labels" id="labels">
        <!-- Labels injected here -->
    </div>
</div>
`;

export class SlideSwitch extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(template.content.cloneNode(true));
        this.container = this.shadowRoot.getElementById('container');
        this.thumb = this.shadowRoot.getElementById('thumb');
        this.labelsContainer = this.shadowRoot.getElementById('labels');
        this.valDisplay = this.shadowRoot.getElementById('val-display');
        
        this._options = []; 
        this._value = 0; 
        this._titles = [];

        this.container.addEventListener('click', (e) => {
             const rect = this.container.getBoundingClientRect();
             const x = e.clientX - rect.left;
             const y = e.clientY - rect.top;
             const width = rect.width;
             const height = rect.height;
             const count = this._options.length;
             if (count === 0) return;
             
             const cols = parseInt(this.getAttribute('cols')) || count;
             const rows = Math.ceil(count / cols);
             
             const colWidth = width / cols;
             const rowHeight = height / rows;
             
             const col = Math.floor(x / colWidth);
             const row = Math.floor(y / rowHeight);
             
             const index = row * cols + col;
             
             if (index >= 0 && index < count) {
                this.value = index;
             }
        });
    }

    static get observedAttributes() {
        return ['labels', 'titles', 'value', 'width', 'height', 'color', 'cols', 'data-default'];
    }

    get min() { return 0; }
    get max() { return Math.max(0, this._options.length - 1); }
    get step() { return 1; }

    attributeChangedCallback(name, oldVal, newVal) {
        if (name === 'labels') {
            this._options = newVal.split('|').map(s => ({ label: s.trim(), value: s.trim() }));
            this.renderLabels();
            this.updateThumb();
        } else if (name === 'titles') {
            this._titles = newVal.split('|').map(s => s.trim());
            this.renderLabels(); // Refresh titles
            this.updateDisplay();
        } else if (name === 'value') {
            const val = parseInt(newVal);
            if (!isNaN(val) && val !== this._value) {
                this._value = val;
                this.updateThumb();
                this.updateDisplay();
            }
        } else if (name === 'width') {
            this.style.setProperty('--width', newVal);
        } else if (name === 'height') {
            this.style.setProperty('--height', newVal);
        } else if (name === 'color') {
             this.style.setProperty('--active-color', newVal);
        } else if (name === 'cols') {
             this.style.setProperty('--cols', newVal);
             this.updateThumb();
        }
    }
    
    get value() { return this._value; }
    set value(val) {
        if (val === this._value) return;
        this._value = val;
        this.setAttribute('value', val);
        this.updateThumb();
        this.updateDisplay();
        this.dispatchEvent(new Event('change', { bubbles: true }));
        this.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateDisplay() {
        if (this._titles[this._value]) {
            this.valDisplay.textContent = this._titles[this._value];
        } else if (this._options[this._value]) {
            // Fallback to text if label is not SVG
            const label = this._options[this._value].label;
            if (!label.includes('<svg')) {
                this.valDisplay.textContent = label;
            } else {
                this.valDisplay.textContent = `Mode ${this._value}`;
            }
        }
    }

    renderLabels() {
        this.labelsContainer.innerHTML = '';
        this._options.forEach((opt, i) => {
            const div = document.createElement('div');
            div.className = 'label';
            div.innerHTML = opt.label;
            if (i === this._value) div.classList.add('active');
            
            if (this._titles[i]) {
                div.setAttribute('title', this._titles[i]);
                
                // Dynamic display on hover
                div.addEventListener('mouseenter', () => {
                    this.valDisplay.textContent = this._titles[i];
                });
                div.addEventListener('mouseleave', () => {
                    this.updateDisplay();
                });
            }

            this.labelsContainer.appendChild(div);
        });
        
        // Auto-cols if not set
        if (!this.hasAttribute('cols')) {
            this.style.setProperty('--cols', this._options.length);
        }
        this.updateDisplay();
    }

    updateThumb() {
        const count = this._options.length;
        if (count === 0) return;
        
        const cols = parseInt(this.getAttribute('cols')) || count;
        const rows = Math.ceil(count / cols);
        
        const widthPct = 100 / cols;
        const heightPct = 100 / rows;
        
        this.thumb.style.width = `calc(${widthPct}% - 4px)`;
        this.thumb.style.height = `calc(${heightPct}% - 4px)`;
        
        const col = this._value % cols;
        const row = Math.floor(this._value / cols);
        
        this.thumb.style.left = `calc(${col * widthPct}% + 2px)`;
        this.thumb.style.top = `calc(${row * heightPct}% + 2px)`;
        
        // Update active class on labels
        const labels = this.labelsContainer.querySelectorAll('.label');
        labels.forEach((l, i) => {
            if (i === this._value) l.classList.add('active');
            else l.classList.remove('active');
        });
    }
}

customElements.define('slide-switch', SlideSwitch);